import { FileImage, FileText, Film, Layers, Mail, MessageSquare, Ruler, ShieldCheck, Sparkles } from "lucide-react";
import { Fade, MaskedLines, Reveal } from "@/components/motion";
import { IMG, SITE } from "@/lib/site";

const BENEFITS = [
    {
        icon: Sparkles,
        title: "A bespoke showcase, not a template",
        text: "Your project page is crafted from the material you share — its own pacing, gallery, plans and story. No two Sthaan pages look the same.",
    },
    {
        icon: MessageSquare,
        title: "Enquiries arrive directly",
        text: "Interested buyers reach your own WhatsApp with a pre-filled, project-specific message. No middle layer, no lead resale.",
    },
    {
        icon: ShieldCheck,
        title: "A curated neighbourhood",
        text: "Your project sits beside a small set of verified new launches — never beside resale listings, rentals or classifieds.",
    },
];

const MATERIALS = [
    { icon: FileText, label: "Brochures & documents" },
    { icon: FileImage, label: "Photography & renders" },
    { icon: Film, label: "Films & walkthroughs" },
    { icon: Ruler, label: "Floor & unit plans" },
    { icon: Layers, label: "Master & site plans" },
    { icon: ShieldCheck, label: "RERA & approval details" },
    { icon: Sparkles, label: "Amenities & specifications" },
    { icon: MessageSquare, label: "Your story & portfolio" },
];

const STEPS = [
    {
        n: "01",
        title: "Write to us",
        text: `Email ${SITE.email} with your project name, city and whatever material you have — even just your website.`,
    },
    {
        n: "02",
        title: "We craft your page",
        text: "We verify the essentials, then design a project-specific showcase around your genuine material. Nothing is invented or padded.",
    },
    {
        n: "03",
        title: "Review & go live",
        text: "You approve the final showcase before it appears on Sthaan, ready to receive direct buyer enquiries.",
    },
];

export default function Developers() {
    return (
        <div data-testid="developers-page">
            <section className="relative flex min-h-[70vh] items-end overflow-hidden">
                <img
                    src={IMG.hero_commercial}
                    alt="Premium architecture"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-obsidian/25" />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(100deg, rgba(250,248,244,0.97) 0%, rgba(250,248,244,0.82) 45%, rgba(250,248,244,0.3) 78%)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-44 sm:px-8">
                    <Fade delay={0.1}>
                        <p className="eyebrow">For Developers</p>
                    </Fade>
                    <h1 className="mt-5 font-serif text-4xl font-light leading-[1.06] tracking-tight text-ivory sm:text-6xl">
                        <MaskedLines lines={["Your project,", "presented like it deserves."]} />
                    </h1>
                    <Fade delay={0.8}>
                        <p className="mt-6 max-w-xl text-sm leading-relaxed text-mist sm:text-base">
                            Sthaan is India's design-led discovery platform for new
                            residential and commercial projects. Buyers explore your project
                            as a cinematic showcase — and contact you directly, with zero
                            brokerage between you.
                        </p>
                    </Fade>
                    <Fade delay={1}>
                        <a
                            href={`mailto:${SITE.email}?subject=${encodeURIComponent("List a project on Sthaan")}`}
                            data-testid="developers-email-cta"
                            className="mt-9 inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-base font-semibold text-obsidian transition-all hover:-translate-y-0.5 hover:bg-gold-bright"
                        >
                            <Mail className="h-5 w-5" />
                            {SITE.email}
                        </a>
                    </Fade>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
                <div className="grid gap-px overflow-hidden rounded-3xl border border-black/8 bg-black/5 md:grid-cols-3">
                    {BENEFITS.map((b) => (
                        <div key={b.title} className="bg-card p-8 sm:p-10">
                            <b.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                            <h3 className="mt-6 font-serif text-2xl text-ivory">{b.title}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-mist">{b.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="border-y border-black/5 bg-surface/40">
                <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
                    <Reveal>
                        <p className="eyebrow">Bring Whatever You Have</p>
                        <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-ivory sm:text-4xl">
                            Every project is different. Your page will be too.
                        </h2>
                        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-mist">
                            One developer may share a brochure and thirty photographs;
                            another, films, villa plans and a payment schedule. Sthaan
                            shapes each showcase around what's real and available — and
                            simply omits what isn't. Nothing is ever invented.
                        </p>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
                        {MATERIALS.map((m, i) => (
                            <Reveal key={m.label} delay={(i % 4) * 0.06}>
                                <div className="flex h-full items-center gap-3 rounded-2xl border border-black/8 bg-card p-5">
                                    <m.icon className="h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                                    <p className="text-sm text-mist">{m.label}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
                <Reveal>
                    <p className="eyebrow">How It Works</p>
                    <h2 className="mt-4 font-serif text-3xl leading-tight text-ivory sm:text-4xl">
                        Three steps to your showcase.
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-8 md:grid-cols-3">
                    {STEPS.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.1}>
                            <div className="border-t border-gold/30 pt-6">
                                <p className="font-mono text-xs text-gold">{s.n}</p>
                                <h3 className="mt-3 font-serif text-2xl text-ivory">{s.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-mist">{s.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
                <Reveal>
                    <div className="gold-ring rounded-3xl bg-surface/60 p-10 text-center sm:p-14">
                        <p className="eyebrow">Get In Touch</p>
                        <h2 className="mx-auto mt-4 max-w-xl font-serif text-3xl leading-tight text-ivory sm:text-4xl">
                            One email starts your showcase.
                        </h2>
                        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-mist">
                            Write to us with your project name, city and whatever material
                            you have — even just your website. We'll take it from there.
                        </p>
                        <a
                            href={`mailto:${SITE.email}?subject=${encodeURIComponent("List a project on Sthaan")}`}
                            data-testid="developers-email-footer-cta"
                            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-base font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-gold-bright"
                        >
                            <Mail className="h-5 w-5" />
                            {SITE.email}
                        </a>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
